// Mirrors backend/src/services/reprocessPermission.util.js - keep the two in step.
// The server is the final authority: this only decides whether the Reprocess button is shown.
import { Report, ReportStatus, User } from '../types';

// Admin / super_admin: exactly the rule the UI and API had before (unchanged).
const ADMIN_REPROCESS_STATUSES: ReportStatus[] = ['error', 'uploaded', 'ready'];

// Nurse: her OWN reports whose data has not been pushed to the external HIMS DB.
// 'approved' and 'rejected' are included: for those two the server also confirms with HIMS
// that no rows exist for the order and refuses (409 / 503) otherwise - the UI shows its message.
// Never 'processing' (a run is in flight) or 'published' (data is in HIMS), and never a report
// that was published at least once (publishedAt set).
// NOTE for the product owner: this is wider than the admin rule above (admins still cannot
// reprocess approved/rejected unless flagged incomplete).
const NURSE_REPROCESS_STATUSES: ReportStatus[] = ['uploaded', 'error', 'ready', 'approved', 'rejected'];

export type ReprocessCheckReport = Pick<Report, 'status' | 'uploadedBy' | 'processingIssues'> & {
  publishedAt?: string | null;
};

const ownerIdOf = (report: Pick<Report, 'uploadedBy'>): string | null => {
  const owner: any = report.uploadedBy;
  if (!owner) return null;
  const id = typeof owner === 'string' ? owner : (owner._id ?? owner.id);
  return id ? String(id) : null;
};

export const canReprocessReport = (user: User | null, report: ReprocessCheckReport): boolean => {
  if (!user) return false;
  const incomplete = !!report.processingIssues?.hasIncompleteProcessing;

  if (user.role === 'admin' || user.role === 'super_admin') {
    return ADMIN_REPROCESS_STATUSES.includes(report.status) || incomplete;
  }

  if (user.role !== 'nurse') return false;

  const ownerId = ownerIdOf(report);
  if (!ownerId || ownerId !== String(user.id)) return false;

  // Hard stops for a nurse, even when flagged incomplete
  if (report.status === 'published' || report.status === 'processing') return false;
  if (report.publishedAt) return false;

  return NURSE_REPROCESS_STATUSES.includes(report.status);
};

// Mirrors deleteReport in backend/src/controllers/report.controller.js (unchanged there).
// Admin / super_admin: any report except approved/published.
// Nurse: her OWN reports while uploaded, processing, error or ready (never approved/rejected/published).
const NURSE_DELETE_STATUSES: ReportStatus[] = ['uploaded', 'processing', 'error', 'ready'];

export const canDeleteReport = (user: User | null, report: Pick<Report, 'status' | 'uploadedBy'>): boolean => {
  if (!user) return false;

  if (user.role === 'admin' || user.role === 'super_admin') {
    return report.status !== 'approved' && report.status !== 'published';
  }

  if (user.role !== 'nurse') return false;

  const ownerId = ownerIdOf(report);
  if (!ownerId || ownerId !== String(user.id)) return false;

  return NURSE_DELETE_STATUSES.includes(report.status);
};
